'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play,  } from 'lucide-react';


import { songService, Song } from '@/lib/services/radioService';
import { createClient } from '@/lib/supabase/client';

// ── DJ Jorge ElevenLabs config ────────────────────────────────────────────────
const JORGE_VOICE_ID = 'YExhVa4bZONzeingloMX';
const ELEVENLABS_MODEL = 'eleven_multilingual_v2';
const DJ_INTERVAL = 3; // announce every N songs
const JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/ElevenLabs_2026-07-28T23_18_24_Charlie-Deep-Confident-Energetic_pre_sp100_s50_sb75_v3-1.mp3';
const WEATHER_INTERVAL = 5; // play weather announcement every N songs
const STATION_JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/BestStationOnThePlanet1-1.mp3';
const STATION_JINGLE_INTERVAL = 10; // play station jingle every N songs
const HITS_JINGLE_URL = 'https://rumbaexplosion.co/wp-content/uploads/2024/10/PlayingNuttinButDaHits1-2.mp3';
const HITS_JINGLE_INTERVAL = 10; // play hits jingle every N songs
// ── News bulletin config ──────────────────────────────────────────────────────
const NEWS_BULLETIN_HOUR = 2; // 12:30PM Medellín time (America/Bogota = UTC-5)
const NEWS_BULLETIN_STORAGE_KEY = 'rumba_news_bulletin_last_played_date';

// ── Top Songs Show config ─────────────────────────────────────────────────────
// Airs at 12:00 PM and 6:00 PM Medellín time every day
const TOP_SONGS_SHOW_SLOTS = [
  { hour: 12, minute: 0 },
  { hour: 15, minute: 0 },
  { hour: 18, minute: 0 },
];
const TOP_SONGS_PLAYED_KEY = 'rumba_top_songs_show_played_slots';

// ── Direct MP3 URL for news bulletin ─────────────────────────────────────────
const NEWS_BULLETIN_MP3_URL = 'https://decisive-canary-357.convex.cloud/api/storage/4cb8891a-e731-44dc-90c7-903c74d1f90d';

// ── Fetch latest news MP3 URL from Supabase ───────────────────────────────────
async function fetchNewsMp3UrlFromSupabase(): Promise<string> {
  try {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('station_settings')
      .select('news_mp3_url')
      .limit(1)
      .single();
    if (!error && data?.news_mp3_url && data.news_mp3_url.trim() !== '') {
      return data.news_mp3_url.trim();
    }
  } catch {
    // fall through to default
  }
  return NEWS_BULLETIN_MP3_URL;
}

// ── Check if news bulletin should play ───────────────────────────────────────
// Reads scheduled slots from localStorage (set by NewsBulletinContent).
// Falls back to the hardcoded 12:00 PM daily trigger when no slots are stored.
// Uses a 3-minute trigger window to avoid missing slots due to check interval timing.
const NEWS_BULLETIN_PLAYED_KEY = 'rumba_news_bulletin_played_slots';

function shouldPlayNewsBulletin(): boolean {
  if (typeof window === 'undefined') return false;

  // Get current Medellín time (America/Bogota = UTC-5, no DST)
  const now = new Date();
  const medellinOffset = -5 * 60;
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const medellinDate = new Date(utcMs + medellinOffset * 60000);

  const hour = medellinDate.getHours();
  const minute = medellinDate.getMinutes();
  const todayStr = `${medellinDate.getFullYear()}-${String(medellinDate.getMonth() + 1).padStart(2, '0')}-${String(medellinDate.getDate()).padStart(2, '0')}`;
  const nowTotalMin = hour * 60 + minute;

  // ── Check scheduled slots (every-30-min mode) ──────────────────────────
  const slotsRaw = localStorage.getItem('rumba_news_bulletin_schedule_slots');
  if (slotsRaw) {
    try {
      const slots: { date: string; hour: number; minute: number }[] = JSON.parse(slotsRaw);
      if (slots.length > 0) {
        const next = slots[0];
        const slotTotalMin = next.hour * 60 + next.minute;
        const slotKey = `${next.date}_${String(next.hour).padStart(2,'0')}${String(next.minute).padStart(2,'0')}`;

        // Trigger within a 3-minute window AFTER the scheduled time (0 to +3 min)
        const isWithinWindow = next.date === todayStr &&
          nowTotalMin >= slotTotalMin &&
          nowTotalMin <= slotTotalMin + 3;

        if (isWithinWindow) {
          // Check if we already played this slot
          const playedRaw = localStorage.getItem(NEWS_BULLETIN_PLAYED_KEY);
          const played: string[] = playedRaw ? JSON.parse(playedRaw) : [];
          if (played.includes(slotKey)) {
            // Already played — prune if slot is now > 5 min past
            if (nowTotalMin > slotTotalMin + 5) {
              const remaining = slots.slice(1);
              if (remaining.length > 0) {
                localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
              } else {
                localStorage.removeItem('rumba_news_bulletin_schedule_slots');
              }
            }
            return false;
          }

          // Mark as played immediately to prevent double-firing
          played.push(slotKey);
          // Keep only last 10 played keys to avoid unbounded growth
          const trimmed = played.slice(-10);
          localStorage.setItem(NEWS_BULLETIN_PLAYED_KEY, JSON.stringify(trimmed));

          // Remove this slot from the queue
          const remaining = slots.slice(1);
          if (remaining.length > 0) {
            localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
          } else {
            localStorage.removeItem('rumba_news_bulletin_schedule_slots');
          }
          console.log(`[News Bulletin] Scheduled slot triggered: ${next.date} ${String(next.hour).padStart(2,'0')}:${String(next.minute).padStart(2,'0')} (now ${String(hour).padStart(2,'0')}:${String(minute).padStart(2,'0')})`);
          return true;
        }

        // Remove stale past slots (more than 5 minutes past)
        const slotIsPast = next.date < todayStr || (next.date === todayStr && slotTotalMin < nowTotalMin - 5);
        if (slotIsPast) {
          const remaining = slots.slice(1);
          if (remaining.length > 0) {
            localStorage.setItem('rumba_news_bulletin_schedule_slots', JSON.stringify(remaining));
          } else {
            localStorage.removeItem('rumba_news_bulletin_schedule_slots');
          }
        }
        return false;
      }
    } catch {
      localStorage.removeItem('rumba_news_bulletin_schedule_slots');
    }
  }

  // ── Fallback: hardcoded 12:00 PM daily trigger ─────────────────────────
  if (hour !== NEWS_BULLETIN_HOUR || minute !== 0) return false;

  const lastPlayed = localStorage.getItem(NEWS_BULLETIN_STORAGE_KEY);
  if (lastPlayed === todayStr) return false;

  localStorage.setItem(NEWS_BULLETIN_STORAGE_KEY, todayStr);
  return true;
}

// ── Check if Top Songs Show should play (12:00 PM and 6:00 PM daily) ─────────
function shouldPlayTopSongsShow(): boolean {
  if (typeof window === 'undefined') return false;

  const now = new Date();
  const medellinOffset = -5 * 60;
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const medellinDate = new Date(utcMs + medellinOffset * 60000);

  const hour = medellinDate.getHours();
  const minute = medellinDate.getMinutes();
  const todayStr = `${medellinDate.getFullYear()}-${String(medellinDate.getMonth() + 1).padStart(2, '0')}-${String(medellinDate.getDate()).padStart(2, '0')}`;
  const nowTotalMin = hour * 60 + minute;

  const playedRaw = localStorage.getItem(TOP_SONGS_PLAYED_KEY);
  const played: string[] = playedRaw ? JSON.parse(playedRaw) : [];

  for (const slot of TOP_SONGS_SHOW_SLOTS) {
    const slotTotalMin = slot.hour * 60 + slot.minute;
    const slotKey = `${todayStr}_${String(slot.hour).padStart(2, '0')}${String(slot.minute).padStart(2, '0')}`;

    // Trigger within a 3-minute window AFTER the scheduled time
    const isWithinWindow = nowTotalMin >= slotTotalMin && nowTotalMin <= slotTotalMin + 3;

    if (isWithinWindow && !played.includes(slotKey)) {
      played.push(slotKey);
      // Keep only last 20 played keys
      const trimmed = played.slice(-20);
      localStorage.setItem(TOP_SONGS_PLAYED_KEY, JSON.stringify(trimmed));
      console.log(`[Top Songs Show] Slot triggered: ${String(slot.hour).padStart(2, '0')}:${String(slot.minute).padStart(2, '0')}`);
      return true;
    }
  }
  return false;
}

// ── Top Songs Show: song info returned from API ───────────────────────────────
interface TopShowSong {
  id: string;
  title: string;
  artist: string;
  play_count: number;
  file_url?: string;
}

// ── Fetch top songs list from API ─────────────────────────────────────────────
async function fetchTopSongsList(): Promise<TopShowSong[]> {
  try {
    const controller = new AbortController();
    let timeoutId = setTimeout(() => controller.abort(), 15000);
    const res = await fetch('/api/top-songs-tts?info=1', { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) return [];
    const data = await res.json();
    return (data.songs ?? []) as TopShowSong[];
  } catch {
    return [];
  }
}

// ── Generate a TTS blob URL for a specific API endpoint ───────────────────────
async function generateTopShowTTS(url: string): Promise<string | null> {
  try {
    const controller = new AbortController();
    let timeoutId = setTimeout(() => controller.abort(), 45000);
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    if (!res.ok) {
      console.error('[Top Songs Show] TTS error:', res.status, url);
      return null;
    }
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch (err) {
    console.error('[Top Songs Show] TTS fetch error:', err);
    return null;
  }
}

// ── Play a blob URL on a given audio element (returns a Promise) ──────────────
function playAudioBlob(audio: HTMLAudioElement, blobUrl: string, volume: number, muted: boolean): Promise<void> {
  return new Promise((resolve) => {
    audio.src = blobUrl;
    audio.volume = muted ? 0 : Math.max(0, Math.min(1, volume / 100));
    audio.currentTime = 0;

    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
    };

    audio.onended = () => { cleanup(); resolve(); };
    audio.onerror = () => { cleanup(); resolve(); };

    audio.load();
    const onCanPlay = () => {
      audio.removeEventListener('canplay', onCanPlay);
      audio.play().catch(() => resolve());
    };
    audio.addEventListener('canplay', onCanPlay, { once: true });
  });
}

// ── Play a song file URL on the main audio element (returns a Promise) ────────
function playSongFile(audio: HTMLAudioElement, fileUrl: string, volume: number, muted: boolean): Promise<void> {
  return new Promise((resolve) => {
    audio.src = fileUrl;
    audio.volume = muted ? 0 : Math.max(0, Math.min(1, volume / 100));
    audio.currentTime = 0;

    const cleanup = () => {
      audio.onended = null;
      audio.onerror = null;
    };

    audio.onended = () => { cleanup(); resolve(); };
    audio.onerror = () => { cleanup(); resolve(); };

    audio.load();
    const onCanPlay = () => {
      audio.removeEventListener('canplay', onCanPlay);
      audio.play().catch(() => resolve());
    };
    audio.addEventListener('canplay', onCanPlay, { once: true });
  });
}

// ── Medellín weather data fetcher ─────────────────────────────────────────────
const WEATHER_LAT = 6.2442;
const WEATHER_LON = -75.5812;

interface WeatherCondition {
  temperature: number;
  feelsLike: number;
  humidity: number;
  windspeed: number;
  weatherCode: number;
  isDay: number;
}

function getWeatherLabel(code: number, isDay: number): string {
  if (code === 0) return isDay ? 'cielo despejado' : 'noche clara';
  if (code <= 2) return 'parcialmente nublado';
  if (code === 3) return 'nublado';
  if (code <= 49) return 'con niebla';
  if (code <= 59) return 'con llovizna';
  if (code <= 69) return 'con lluvia';
  if (code <= 79) return 'con nieve';
  if (code <= 82) return 'con aguacero';
  if (code <= 84) return 'con granizo';
  if (code <= 99) return 'con tormenta eléctrica';
  return 'variable';
}

async function fetchMedellinWeather(): Promise<WeatherCondition | null> {
  try {
    const weatherController = new AbortController();
    const weatherTimeoutId = setTimeout(() => weatherController.abort(), 6000);
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${WEATHER_LAT}&longitude=${WEATHER_LON}&current=temperature_2m,apparent_temperature,relative_humidity_2m,wind_speed_10m,weather_code,is_day&timezone=America%2FBogota`,
      { signal: weatherController.signal }
    );
    clearTimeout(weatherTimeoutId);
    if (!res.ok) return null;
    const json = await res.json();
    const c = json.current;
    return {
      temperature: Math.round(c.temperature_2m),
      feelsLike: Math.round(c.apparent_temperature),
      humidity: c.relative_humidity_2m,
      windspeed: Math.round(c.wind_speed_10m),
      weatherCode: c.weather_code,
      isDay: c.is_day,
    };
  } catch {
    return null;
  }
}

async function generateWeatherAnnouncement(): Promise<string | null> {
  const apiKey = process.env.NEXT_PUBLIC_ELEVENLABS_API_KEY;
  if (!apiKey) {
    console.warn('[Weather TTS] No ElevenLabs API key found');
    return null;
  }

  const weather = await fetchMedellinWeather();
  if (!weather) {
    console.warn('[Weather TTS] Could not fetch weather data');
    return null;
  }

  const label = getWeatherLabel(weather.weatherCode, weather.isDay);
  const timeOfDay = weather.isDay ? 'día' : 'noche';

  const text = `Y ahora, el reporte del clima para Medellín, la Ciudad de la Eterna Primavera. En este momento tenemos ${label}, con una temperatura de ${weather.temperature} grados centígrados. La sensación térmica es de ${weather.feelsLike} grados. La humedad relativa está al ${weather.humidity} por ciento, y los vientos soplan a ${weather.windspeed} kilómetros por hora. Disfruten esta hermosa ${timeOfDay} en Medellín. Seguimos con más música en Rumbaexplosions.`;

  try {
    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${JORGE_VOICE_ID}`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          model_id: ELEVENLABS_MODEL,
          voice_settings: {
            stability: 0.4,
            similarity_boost: 1.0,
            style: 0.0,
            use_speaker_boost: true,
          },
          output_format: 'mp3_44100_128',
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[Weather TTS] ElevenLabs API error:', res.status, errText);
      return null;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[Weather TTS] Audio blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[Weather TTS] fetch error:', err);
    return null;
  }
}

async function generateJorgeAnnouncement(nextSong: Song): Promise<string | null> {
  const apiKey = process.env.NEXT_PUBLIC_ELEVENLABS_API_KEY;
  if (!apiKey) {
    console.warn('[DJ Jorge] No ElevenLabs API key found');
    return null;
  }

  const text = `¡Qué tal, amigos! Aquí Jorge, su DJ favorito en Rumbaexplosions, la estación que hace vibrar tu corazón. A continuación, les traigo una joya musical: "${nextSong.title}" de ${nextSong.artist}. ¡Que lo disfruten!`;

  try {
    const res = await fetch(
      `https://api.elevenlabs.io/v1/text-to-speech/${JORGE_VOICE_ID}`,
      {
        method: 'POST',
        headers: {
          'xi-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          model_id: ELEVENLABS_MODEL,
          voice_settings: {
            stability: 0.4,
            similarity_boost: 1.0,
            style: 0.0,
            use_speaker_boost: true,
          },
          output_format: 'mp3_44100_128',
        }),
      }
    );

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      console.error('[DJ Jorge] ElevenLabs API error:', res.status, errText);
      return null;
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[DJ Jorge] Audio blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[DJ Jorge] fetch error:', err);
    return null;
  }
}

// ── Generate News Bulletin TTS via ElevenLabs ─────────────────────────────────
async function generateNewsBulletin(): Promise<string | null> {
  // Call the server-side news-tts route which fetches live headlines + generates fresh TTS audio
  try {
    console.log('[News Bulletin] Fetching fresh news TTS from /api/news-tts…');
    const newsController = new AbortController();
    const newsTimeoutId = setTimeout(() => newsController.abort(), 30000);
    const res = await fetch('/api/news-tts', { signal: newsController.signal });
    clearTimeout(newsTimeoutId);
    if (!res.ok) {
      const errJson = await res.json().catch(() => ({}));
      console.error('[News Bulletin] /api/news-tts error:', res.status, errJson);
      return null;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    console.log('[News Bulletin] Fresh TTS blob URL created:', url);
    return url;
  } catch (err) {
    console.error('[News Bulletin] fetch error:', err);
    return null;
  }
}

// ── iTunes artwork fetcher ────────────────────────────────────────────────────
async function fetchItunesArtwork(artist: string, title: string): Promise<string> {
  try {
    const q = encodeURIComponent(`${artist} ${title}`.trim());
    const itunesController = new AbortController();
    const itunesTimeoutId = setTimeout(() => itunesController.abort(), 4000);
    const res = await fetch(
      `https://itunes.apple.com/search?term=${q}&media=music&entity=song&limit=1`,
      { signal: itunesController.signal }
    );
    clearTimeout(itunesTimeoutId);
    if (!res.ok) return '';
    const json = await res.json();
    const result = json?.results?.[0];
    if (!result?.artworkUrl100) return '';
    return result.artworkUrl100.replace('100x100bb', '600x600bb');
  } catch {
    return '';
  }
}

// ── Deterministic gradient fallback ──────────────────────────────────────────
function getArtColor(str: string): [string, string] {
  const colors: [string, string][] = [
    ['#7c3aed', '#a855f7'],
    ['#2563eb', '#7c3aed'],
    ['#db2777', '#7c3aed'],
    ['#059669', '#2563eb'],
    ['#d97706', '#db2777'],
    ['#0891b2', '#059669'],
    ['#7c3aed', '#db2777'],
    ['#1d4ed8', '#0891b2'],
  ];
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

function getInitials(artist: string, title: string): string {
  return (artist || title || '?')
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function PublicPlayerHero() {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const nextAudioRef = useRef<HTMLAudioElement | null>(null);
  const djAudioRef = useRef<HTMLAudioElement | null>(null);
  const jingleAudioRef = useRef<HTMLAudioElement | null>(null);
  const stationJingleAudioRef = useRef<HTMLAudioElement | null>(null);
  const hitsJingleAudioRef = useRef<HTMLAudioElement | null>(null);

  const [songs, setSongs] = useState<Song[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(80);
  const [muted, setMuted] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [djSpeaking, setDjSpeaking] = useState(false);
  const [weatherSpeaking, setWeatherSpeaking] = useState(false);
  const [newsSpeaking, setNewsSpeaking] = useState(false);
  // Gate autoplay behind a user gesture to comply with browser autoplay policy
  const [userHasInteracted, setUserHasInteracted] = useState(false);
  // Track whether the initial songs fetch has completed (success or failure)
  const [songsLoaded, setSongsLoaded] = useState(false);

  // artwork: keyed by song id → resolved URL (or '' for fallback)
  const [artworkCache, setArtworkCache] = useState<Record<string, string>>({});
  const fetchingRef = useRef<Set<string>>(new Set());

  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const songPlayCountRef = useRef(0);
  const djBlobUrlRef = useRef<string | null>(null);
  const weatherSongCountRef = useRef(0);
  const stationJingleCountRef = useRef(0);
  const hitsJingleCountRef = useRef(0);
  const earlyPrebufferRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Stable refs for volume and muted — avoids recreating loadAndPlay on every volume change
  const volumeRef = useRef(80);
  const mutedRef = useRef(false);

  // Pre-fetched TTS blobs — generated during current track to eliminate gap
  const prefetchedDjBlobRef = useRef<string | null>(null);
  const prefetchedWeatherBlobRef = useRef<string | null>(null);
  const prefetchedNewsBlobRef = useRef<string | null>(null);
  const ttsPreFetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const weatherAudioRef = useRef<HTMLAudioElement | null>(null);
  const weatherBlobUrlRef = useRef<string | null>(null);

  // ── News bulletin refs ────────────────────────────────────────────────────
  const newsAudioRef = useRef<HTMLAudioElement | null>(null);
  const newsBlobUrlRef = useRef<string | null>(null);
  const newsBulletinPendingRef = useRef(false);
  const newsCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPlayingNewsRef = useRef(false);
  const newsMp3UrlRef = useRef<string>(NEWS_BULLETIN_MP3_URL);
  const newsMp3UrlLastFetchRef = useRef<number>(0);

  // ── Top Songs Show refs ───────────────────────────────────────────────────
  const topSongsAudioRef = useRef<HTMLAudioElement | null>(null);
  const topSongsBlobUrlRef = useRef<string | null>(null);
  const topShowPendingRef = useRef(false);
  const [topShowSpeaking, setTopShowSpeaking] = useState(false);
  const isPlayingTopShowRef = useRef(false);

  // ── Near-end detection guard (prevents double-advance) ───────────────────
  const nearEndFiredRef = useRef(false);
  // ── Crossfade state ───────────────────────────────────────────────────────
  const crossfadeAnimRef = useRef<number | null>(null);
  // ── Music killer: actively suppresses music playback during news ──────────
  // Runs every 300ms while news is active; forcefully pauses main audio.
  const musicKillerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // ── Watchdog: detects silent stalls and auto-recovers ─────────────────────
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastKnownTimeRef = useRef<number>(0);
  const lastKnownTimeStampRef = useRef<number>(0);
  // ── Reconnect attempt counter (reset on successful playback advance) ──────
  const reconnectAttemptsRef = useRef<number>(0);
  const MAX_RECONNECT_ATTEMPTS = 3;

  // ── Fetch songs ───────────────────────────────────────────────────────────
  useEffect(() => {
    songService.getAll().then((all) => {
      const playable = all.filter((s) => s.fileUrl && s.fileUrl.trim() !== '');
      for (let i = playable.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [playable[i], playable[j]] = [playable[j], playable[i]];
      }
      setSongs(playable);
      setSongsLoaded(true);
    }).catch(() => {
      setSongs([]);
      setSongsLoaded(true);
    });

    // Safety timeout: dismiss loading overlay after 10s regardless of fetch result
    const safetyTimer = setTimeout(() => setSongsLoaded(true), 10000);
    return () => clearTimeout(safetyTimer);
  }, []);

  // ── Fetch and refresh news MP3 URL from Supabase every 30 minutes ─────────
  useEffect(() => {
    const refreshNewsUrl = async () => {
      const url = await fetchNewsMp3UrlFromSupabase();
      newsMp3UrlRef.current = url;
      newsMp3UrlLastFetchRef.current = Date.now();
      console.log('[News Bulletin] Refreshed MP3 URL from Supabase:', url);
    };

    // Fetch immediately on mount
    refreshNewsUrl();

    // Refresh every 30 minutes
    const intervalId = setInterval(refreshNewsUrl, 30 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, []);

  // ── Elapsed ticker ────────────────────────────────────────────────────────
  const startTicker = useCallback(() => {
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    elapsedIntervalRef.current = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) {
        setElapsed(audioRef.current.currentTime);
        setDuration(audioRef.current.duration || 0);
      }
    }, 500);
  }, []);

  const stopTicker = useCallback(() => {
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
  }, []);

  useEffect(() => () => stopTicker(), [stopTicker]);

  // Cleanup music killer on unmount
  useEffect(() => () => {
    if (musicKillerRef.current !== null) {
      clearInterval(musicKillerRef.current);
      musicKillerRef.current = null;
    }
  }, []);

  // Cleanup crossfade animation on unmount
  useEffect(() => () => {
    if (crossfadeAnimRef.current !== null) {
      clearInterval(crossfadeAnimRef.current);
      crossfadeAnimRef.current = null;
    }
  }, []);

  // ── Pre-buffer next track ─────────────────────────────────────────────────
  const prebufferNext = useCallback((nextIndex: number, songList: Song[]) => {
    const nextSong = songList[nextIndex];
    if (!nextSong?.fileUrl) return;
    const nextAudio = nextAudioRef.current;
    if (!nextAudio) return;
    if (nextAudio.src !== nextSong.fileUrl) {
      nextAudio.src = nextSong.fileUrl;
      nextAudio.preload = 'auto';
      nextAudio.load();
    }
  }, []);

  // ── Play Jingle ───────────────────────────────────────────────────────────
  const playJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const jingleAudio = jingleAudioRef.current;
      if (!jingleAudio) { resolve(); return; }

      jingleAudio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      jingleAudio.currentTime = 0;

      const cleanup = () => {
        jingleAudio.onended = null;
        jingleAudio.onerror = null;
      };

      jingleAudio.onended = () => { cleanup(); resolve(); };
      jingleAudio.onerror = () => { cleanup(); resolve(); };

      // Only set src + load if not already loaded
      if (!jingleAudio.src || !jingleAudio.src.includes('ElevenLabs_2026')) {
        jingleAudio.src = JINGLE_URL;
        jingleAudio.load();
      }
      jingleAudio.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Play Station Jingle ───────────────────────────────────────────────────
  const playStationJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const stationJingle = stationJingleAudioRef.current;
      if (!stationJingle) { resolve(); return; }

      stationJingle.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      stationJingle.currentTime = 0;

      const cleanup = () => {
        stationJingle.onended = null;
        stationJingle.onerror = null;
      };

      stationJingle.onended = () => { cleanup(); resolve(); };
      stationJingle.onerror = () => { cleanup(); resolve(); };

      if (!stationJingle.src || !stationJingle.src.includes('BestStation')) {
        stationJingle.src = STATION_JINGLE_URL;
        stationJingle.load();
      }
      stationJingle.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Play Hits Jingle (every 15 songs) ────────────────────────────────────
  const playHitsJingle = useCallback((volumeLevel: number, isMuted: boolean): Promise<void> => {
    return new Promise((resolve) => {
      const hitsJingle = hitsJingleAudioRef.current;
      if (!hitsJingle) { resolve(); return; }

      hitsJingle.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      hitsJingle.currentTime = 0;

      const cleanup = () => {
        hitsJingle.onended = null;
        hitsJingle.onerror = null;
      };

      hitsJingle.onended = () => { cleanup(); resolve(); };
      hitsJingle.onerror = () => { cleanup(); resolve(); };

      if (!hitsJingle.src || !hitsJingle.src.includes('PlayingNuttin')) {
        hitsJingle.src = HITS_JINGLE_URL;
        hitsJingle.load();
      }
      hitsJingle.play().catch(() => { cleanup(); resolve(); });
    });
  }, []);

  // ── Music killer: start/stop active suppression of music during news ─────
  const startMusicKiller = useCallback(() => {
    if (musicKillerRef.current) clearInterval(musicKillerRef.current);
    musicKillerRef.current = setInterval(() => {
      const a = audioRef.current;
      if (a && !a.paused) {
        console.log('[MusicKiller] Forcing music pause during news');
        a.pause();
      }
      const na = nextAudioRef.current;
      if (na && !na.paused) na.pause();
    }, 300);
  }, []);

  const stopMusicKiller = useCallback(() => {
    if (musicKillerRef.current) {
      clearInterval(musicKillerRef.current);
      musicKillerRef.current = null;
    }
  }, []);

  // ── Play News Bulletin from direct MP3 URL ────────────────────────────────
  const playNewsBulletin = useCallback(async (volumeLevel: number, isMuted: boolean): Promise<void> => {
    console.log('[News Bulletin] Generating fresh Spanish TTS via /api/news-tts…');

    // Mark news as playing so watchdog/visibility handlers don't resume music
    isPlayingNewsRef.current = true;

    // Immediately pause music and start the music killer to prevent any
    // recovery mechanism from restarting it during TTS generation
    if (audioRef.current) audioRef.current.pause();
    if (nextAudioRef.current) { nextAudioRef.current.pause(); nextAudioRef.current.src = ''; }
    startMusicKiller();

    // Generate fresh Spanish TTS audio (same as the scheduler path)
    let blobUrl = await generateNewsBulletin();
    if (!blobUrl) {
      console.warn('[News Bulletin] TTS generation failed, skipping bulletin');
      stopMusicKiller();
      isPlayingNewsRef.current = false;
      return;
    }

    if (newsBlobUrlRef.current) {
      URL.revokeObjectURL(newsBlobUrlRef.current);
    }
    newsBlobUrlRef.current = blobUrl;

    return new Promise((resolve) => {
      const newsAudio = newsAudioRef.current;
      if (!newsAudio) {
        stopMusicKiller();
        isPlayingNewsRef.current = false;
        resolve();
        return;
      }

      newsAudio.src = blobUrl;
      newsAudio.volume = isMuted ? 0 : 1.0;
      newsAudio.currentTime = 0;

      const cleanup = () => {
        newsAudio.onended = null;
        newsAudio.onerror = null;
        stopMusicKiller();
      };

      newsAudio.onended = () => {
        console.log('[News Bulletin] Bulletin finished');
        cleanup();
        setNewsSpeaking(false);
        isPlayingNewsRef.current = false;
        resolve();
      };

      newsAudio.onerror = (e) => {
        console.error('[News Bulletin] Audio playback error:', e);
        cleanup();
        setNewsSpeaking(false);
        isPlayingNewsRef.current = false;
        resolve();
      };

      newsAudio.load();
      const onCanPlay = () => {
        newsAudio.removeEventListener('canplay', onCanPlay);
        // Double-check music is still paused right before news plays
        if (audioRef.current) audioRef.current.pause();
        newsAudio.play().then(() => {
          setNewsSpeaking(true);
          console.log('[News Bulletin] Playing Spanish bulletin now');
        }).catch((err) => {
          console.error('[News Bulletin] play() failed:', err);
          cleanup();
          setNewsSpeaking(false);
          isPlayingNewsRef.current = false;
          resolve();
        });
      };
      newsAudio.addEventListener('canplay', onCanPlay, { once: true });
    });
  }, [startMusicKiller, stopMusicKiller]);

  // ── Play DJ Jorge announcement ────────────────────────────────────────────
  const playDjAnnouncement = useCallback(async (nextSong: Song, volumeLevel: number, isMuted: boolean): Promise<void> => {
    let blobUrl = prefetchedDjBlobRef.current;
    prefetchedDjBlobRef.current = null;

    if (!blobUrl) {
      console.log('[DJ Jorge] No pre-fetched blob, generating now…');
      blobUrl = await generateJorgeAnnouncement(nextSong);
    } else {
      console.log('[DJ Jorge] Using pre-fetched blob — zero gap');
    }

    if (!blobUrl) {
      console.warn('[DJ Jorge] TTS generation failed, skipping announcement');
      return;
    }

    if (djBlobUrlRef.current) {
      URL.revokeObjectURL(djBlobUrlRef.current);
    }
    djBlobUrlRef.current = blobUrl;

    return new Promise((resolve) => {
      const djAudio = djAudioRef.current;
      if (!djAudio) { resolve(); return; }

      djAudio.src = blobUrl!;
      djAudio.volume = isMuted ? 0 : Math.max(0, Math.min(1, volumeLevel / 100));
      djAudio.currentTime = 0;

      const cleanup = () => {
        djAudio.onended = null;
        djAudio.onerror = null;
      };

      djAudio.onended = () => { cleanup(); setDjSpeaking(false); resolve(); };
      djAudio.onerror = () => { cleanup(); setDjSpeaking(false); resolve(); };

      djAudio.load();
      const onCanPlay = () => {
        djAudio.removeEventListener('canplay', onCanPlay);
        djAudio.play().then(() => {
          setDjSpeaking(true);
          console.log('[DJ Jorge] Playing announcement now');
        }).catch((err) => {
          console.error('[DJ Jorge] play() failed:', err);
          setDjSpeaking(false);
          resolve();
        });
      };
      djAudio.addEventListener('canplay', onCanPlay, { once: true });
    });
  }, []);

  // ── News bulletin scheduler ───────────────────────────────────────────────
  useEffect(() => {
    newsCheckIntervalRef.current = setInterval(async () => {
      // Don't fire if top songs show is playing
      if (isPlayingTopShowRef.current) return;

      // Don't fire if news is already playing
      if (isPlayingNewsRef.current) return;

      // Only check when music is actually playing
      if (!audioRef.current || audioRef.current.paused) return;

      if (shouldPlayNewsBulletin()) {
        console.log('[News Bulletin] Scheduled slot matched — stopping music and starting bulletin');

        // ── Step 1: Set the news flag FIRST (blocks all recovery mechanisms) ─
        isPlayingNewsRef.current = true;

        // ── Step 2: Cancel all timers that could restart audio ───────────────
        if (earlyPrebufferRef.current) {
          clearTimeout(earlyPrebufferRef.current);
          earlyPrebufferRef.current = null;
        }
        if (ttsPreFetchTimerRef.current) {
          clearTimeout(ttsPreFetchTimerRef.current);
          ttsPreFetchTimerRef.current = null;
        }
        if (crossfadeAnimRef.current !== null) {
          clearInterval(crossfadeAnimRef.current);
          crossfadeAnimRef.current = null;
        }

        // ── Step 3: Hard-stop music and start the music killer ───────────────
        const audio = audioRef.current;
        const resumeTime = audio ? audio.currentTime : 0;

        if (audio) audio.pause();
        if (nextAudioRef.current) {
          nextAudioRef.current.pause();
          nextAudioRef.current.src = '';
        }
        setIsPlaying(false);
        stopTicker();

        // Start the music killer — actively prevents any mechanism from
        // restarting music while news is generating or playing
        startMusicKiller();

        // ── Step 4: Generate TTS (music killer keeps audio paused) ───────────
        let blobUrl = prefetchedNewsBlobRef.current;
        prefetchedNewsBlobRef.current = null;
        if (!blobUrl) {
          console.log('[News Bulletin] Generating TTS (music suppressed by killer)...');
          blobUrl = await generateNewsBulletin();
        } else {
          console.log('[News Bulletin] Using pre-fetched news blob');
        }

        if (blobUrl) {
          if (newsBlobUrlRef.current) {
            URL.revokeObjectURL(newsBlobUrlRef.current);
          }
          newsBlobUrlRef.current = blobUrl;

          await new Promise<void>((resolve) => {
            const newsAudio = newsAudioRef.current;
            if (!newsAudio) { resolve(); return; }

            newsAudio.src = blobUrl!;
            newsAudio.volume = mutedRef.current ? 0 : 1.0;
            newsAudio.currentTime = 0;

            const cleanup = () => {
              newsAudio.onended = null;
              newsAudio.onerror = null;
            };

            newsAudio.onended = () => {
              console.log('[News Bulletin] Bulletin finished');
              cleanup();
              setNewsSpeaking(false);
              resolve();
            };

            newsAudio.onerror = (e) => {
              console.error('[News Bulletin] Audio playback error:', e);
              cleanup();
              setNewsSpeaking(false);
              resolve();
            };

            newsAudio.load();
            const onCanPlay = () => {
              newsAudio.removeEventListener('canplay', onCanPlay);
              // Ensure music is still dead right before news plays
              if (audioRef.current) audioRef.current.pause();
              newsAudio.play().then(() => {
                console.log('[News Bulletin] Playing bulletin now');
              }).catch((err) => {
                console.error('[News Bulletin] play() failed:', err);
                cleanup();
                setNewsSpeaking(false);
                resolve();
              });
            };
            newsAudio.addEventListener('canplay', onCanPlay, { once: true });
          });
          setNewsSpeaking(false);
        }

        // ── Step 5: Stop killer, clear news flag, resume music ───────────────
        stopMusicKiller();
        isPlayingNewsRef.current = false;

        if (audio) {
          try {
            audio.currentTime = resumeTime;
            await audio.play();
            setIsPlaying(true);
            startTicker();
            console.log('[News Bulletin] Music resumed after bulletin');
          } catch (err) {
            console.error('[News Bulletin] Failed to resume music:', err);
          }
        }
      }
    }, 15000);

    return () => {
      if (newsCheckIntervalRef.current) {
        clearInterval(newsCheckIntervalRef.current);
        newsCheckIntervalRef.current = null;
      }
      stopMusicKiller();
      if (newsBlobUrlRef.current) {
        URL.revokeObjectURL(newsBlobUrlRef.current);
        newsBlobUrlRef.current = null;
      }
    };
  }, [stopTicker, startTicker, startMusicKiller, stopMusicKiller]);

  // ── Top Songs Show scheduler (12:00 PM, 3:00 PM and 6:00 PM daily) ───────
  // Orchestrates: Intro → [Announce #10 → Play song #10] → … → [Announce #1 → Play song #1] → Outro
  useEffect(() => {
    const topShowIntervalId = setInterval(async () => {
      // Don't fire if another show/news is already playing
      if (isPlayingNewsRef.current || isPlayingTopShowRef.current) return;
      if (!shouldPlayTopSongsShow()) return;

      console.log('[Top Songs Show] Scheduled slot matched — starting countdown show');
      isPlayingTopShowRef.current = true;
      setTopShowSpeaking(true);

      // Pause regular music
      if (audioRef.current) audioRef.current.pause();
      if (stopTicker) stopTicker();

      const topAudio = topSongsAudioRef.current;
      const mainAudio = audioRef.current;
      const vol = volumeRef.current;
      const isMuted = mutedRef.current;

      const resumeMusic = () => {
        setTopShowSpeaking(false);
        isPlayingTopShowRef.current = false;
        if (mainAudio) {
          mainAudio.play().catch(() => {});
          if (startTicker) startTicker();
        }
      };

      if (!topAudio) {
        resumeMusic();
        return;
      }

      // ── Step 1: Fetch the top songs list ──────────────────────────────────
      const topSongs = await fetchTopSongsList();
      if (!topSongs || topSongs.length === 0) {
        console.warn('[Top Songs Show] No songs returned, resuming music');
        resumeMusic();
        return;
      }

      // ── Step 2: Play intro announcement ───────────────────────────────────
      console.log('[Top Songs Show] Generating intro TTS…');
      const introBlobUrl = await generateTopShowTTS('/api/top-songs-tts?intro=1');
      if (introBlobUrl) {
        await playAudioBlob(topAudio, introBlobUrl, vol, isMuted);
        URL.revokeObjectURL(introBlobUrl);
      }

      // ── Step 3: Countdown from #10 to #1 ─────────────────────────────────
      // topSongs[0] = #1 (most played), topSongs[9] = #10 (least of top 10)
      // We play from position 10 down to 1
      const totalSongs = Math.min(topSongs.length, 10);
      for (let pos = totalSongs; pos >= 1; pos--) {
        if (!isPlayingTopShowRef.current) break; // safety: allow external abort

        const songIndex = pos - 1; // 0-based index in sorted array
        const song = topSongs[songIndex];
        if (!song) continue;

        console.log(`[Top Songs Show] Announcing position #${pos}: ${song.title} by ${song.artist}`);

        // For position #1: play a special dramatic emphasis BEFORE the regular announcement
        if (pos === 1) {
          console.log('[Top Songs Show] Playing special #1 emphasis build-up…');
          const emphasisBlobUrl = await generateTopShowTTS('/api/top-songs-tts?emphasis=1');
          if (emphasisBlobUrl) {
            await playAudioBlob(topAudio, emphasisBlobUrl, vol, isMuted);
            URL.revokeObjectURL(emphasisBlobUrl);
          }
        }

        // Generate and play the DJ announcement for this position
        const announceBlobUrl = await generateTopShowTTS(`/api/top-songs-tts?announce=${pos}`);
        if (announceBlobUrl) {
          await playAudioBlob(topAudio, announceBlobUrl, vol, isMuted);
          URL.revokeObjectURL(announceBlobUrl);
        }

        // Play the actual song (use file_url from API, or fall back to songs list)
        const fileUrl = song.file_url;
        if (fileUrl && mainAudio) {
          console.log(`[Top Songs Show] Playing song #${pos}: ${song.title}`);
          // Ensure topAudio is silent while main audio plays
          topAudio.pause();
          topAudio.src = '';
          await playSongFile(mainAudio, fileUrl, vol, isMuted);
          // Pause main audio again before next announcement
          mainAudio.pause();
        } else {
          console.warn(`[Top Songs Show] No file_url for song #${pos}: ${song.title}, skipping playback`);
        }
      }

      // ── Step 4: Play outro ────────────────────────────────────────────────
      console.log('[Top Songs Show] Generating outro TTS…');
      const outroBlobUrl = await generateTopShowTTS('/api/top-songs-tts?outro=1');
      if (outroBlobUrl) {
        await playAudioBlob(topAudio, outroBlobUrl, vol, isMuted);
        URL.revokeObjectURL(outroBlobUrl);
      }

      console.log('[Top Songs Show] Countdown show complete, resuming regular music');
      resumeMusic();
    }, 15000);

    return () => {
      clearInterval(topShowIntervalId);
      if (topSongsBlobUrlRef.current) {
        URL.revokeObjectURL(topSongsBlobUrlRef.current);
        topSongsBlobUrlRef.current = null;
      }
    };
  }, [stopTicker, startTicker]);

  // ── Lightweight stream reconnect (no interstitials, no DJ/jingle logic) ──
  const reconnectStream = useCallback(async (index: number, list: Song[]) => {
    const song = list[index];
    if (!song?.fileUrl) return;
    const audio = audioRef.current;
    if (!audio) return;

    console.log('[Reconnect] Attempting stream reconnect for', song.title);
    audio.src = '';
    audio.load();

    await new Promise<void>((res) => setTimeout(res, 800));

    audio.src = song.fileUrl;
    audio.volume = mutedRef.current ? 0 : Math.max(0, Math.min(1, volumeRef.current / 100));
    audio.currentTime = 0;

    const canPlayResult = await new Promise<'ready' | 'error'>((res) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout>;
      const onReady = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        res('ready');
      };
      const onError = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        res('error');
      };
      timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        res('error');
      }, 10000);
      audio.addEventListener('canplay', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });

    if (canPlayResult === 'error') {
      console.warn('[Reconnect] Reconnect failed, falling back to full reload');
      return;
    }

    try {
      await audio.play();
      setIsPlaying(true);
      startTicker();
      console.log('[Reconnect] Stream reconnected successfully');
    } catch (err) {
      console.error('[Reconnect] play() after reconnect failed:', err);
    }
  }, [startTicker]);

  // ── Load and play a song ──────────────────────────────────────────────────
  const loadAndPlay = useCallback(async (index: number, list: Song[]) => {
    const song = list[index];
    if (!song?.fileUrl) {
      console.warn('[Player] No fileUrl for song at index', index);
      return;
    }

    const audio = audioRef.current;
    if (!audio) return;

    setIsLoading(true);
    setLoadError(null);
    nearEndFiredRef.current = false;

    if (earlyPrebufferRef.current) {
      clearTimeout(earlyPrebufferRef.current);
      earlyPrebufferRef.current = null;
    }
    if (ttsPreFetchTimerRef.current) {
      clearTimeout(ttsPreFetchTimerRef.current);
      ttsPreFetchTimerRef.current = null;
    }
    if (crossfadeAnimRef.current !== null) {
      clearInterval(crossfadeAnimRef.current);
      crossfadeAnimRef.current = null;
    }

    const nextAudio = nextAudioRef.current;
    let usePreBuffer = false;
    if (nextAudio && nextAudio.src === song.fileUrl && nextAudio.readyState >= 3) {
      usePreBuffer = true;
      console.log('[Player] Using pre-buffered audio for', song.title);
    }

    if (usePreBuffer && nextAudio) {
      audio.src = nextAudio.src;
      audio.currentTime = 0;
    } else {
      audio.src = song.fileUrl;
      audio.currentTime = 0;
    }

    audio.volume = mutedRef.current ? 0 : Math.max(0, Math.min(1, volumeRef.current / 100));

    const canPlayResult = await new Promise<'ready' | 'error'>((res) => {
      let settled = false;
      let timeoutId: ReturnType<typeof setTimeout>;
      const onReady = () => {
        if (settled) return;
        settled = true;
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        clearTimeout(timeoutId);
        res('ready');
      };
      const onError = () => {
        if (settled) return;
        settled = true;
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        clearTimeout(timeoutId);
        res('error');
      };
      timeoutId = setTimeout(() => {
        if (settled) return;
        settled = true;
        audio.removeEventListener('canplay', onReady);
        audio.removeEventListener('error', onError);
        res('error');
      }, 12000);
      audio.addEventListener('canplay', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.load();
    });

    if (canPlayResult === 'error') {
      console.error('[Player] Failed to load song:', song.title);
      setLoadError(`Failed to load: ${song.title}`);
      setIsLoading(false);
      const nextIdx = (index + 1) % list.length;
      setCurrentIndex(nextIdx);
      loadAndPlay(nextIdx, list);
      return;
    }

    try {
      await audio.play();
      setIsPlaying(true);
      setIsLoading(false);
      startTicker();

      const currentSong = list[index];
      if (currentSong?.id) {
        fetch('/api/top-songs-tts?track=1', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ song_id: currentSong.id }),
        }).catch(() => {});
      }

      const nextIdx = (index + 1) % list.length;
      prebufferNext(nextIdx, list);
      reconnectAttemptsRef.current = 0;

      const scheduleTtsPrefetch = () => {
        if (ttsPreFetchTimerRef.current) clearTimeout(ttsPreFetchTimerRef.current);
        const dur = audio.duration;
        if (!dur || !isFinite(dur)) return;
        const prefetchAt = dur * 0.7;
        const delay = Math.max(0, (prefetchAt - audio.currentTime) * 1000);
        ttsPreFetchTimerRef.current = setTimeout(async () => {
          const nextSong = list[nextIdx];
          if (!nextSong) return;
          songPlayCountRef.current += 1;
          const count = songPlayCountRef.current;
          if (count % DJ_INTERVAL === 0) {
            prefetchedDjBlobRef.current = await generateJorgeAnnouncement(nextSong);
          } else if (weatherSongCountRef.current >= WEATHER_INTERVAL - 1) {
            prefetchedWeatherBlobRef.current = await generateWeatherAnnouncement();
          } else {
            const slotsRaw = localStorage.getItem('rumba_news_bulletin_schedule_slots');
            if (slotsRaw) {
              prefetchedNewsBlobRef.current = await generateNewsBulletin();
            }
          }
        }, delay);
      };

      if (audio.duration && isFinite(audio.duration)) {
        scheduleTtsPrefetch();
      } else {
        audio.addEventListener('loadedmetadata', scheduleTtsPrefetch, { once: true });
      }

      const scheduleNearEnd = () => {
        if (earlyPrebufferRef.current) clearTimeout(earlyPrebufferRef.current);
        const dur = audio.duration;
        if (!dur || !isFinite(dur)) return;
        const nearEndAt = dur * 0.95;
        const delay = Math.max(0, (nearEndAt - audio.currentTime) * 1000);
        earlyPrebufferRef.current = setTimeout(async () => {
          if (nearEndFiredRef.current) return;
          nearEndFiredRef.current = true;
          console.log('[Player] Near end — advancing to next song');

          audio.pause();
          stopTicker();

          const count = songPlayCountRef.current;
          const nextSong = list[nextIdx];

          stationJingleCountRef.current += 1;
          hitsJingleCountRef.current += 1;

          if (stationJingleCountRef.current >= STATION_JINGLE_INTERVAL) {
            stationJingleCountRef.current = 0;
            await playStationJingle(volumeRef.current, mutedRef.current);
          } else if (hitsJingleCountRef.current >= HITS_JINGLE_INTERVAL) {
            hitsJingleCountRef.current = 0;
            await playHitsJingle(volumeRef.current, mutedRef.current);
          } else if (count % DJ_INTERVAL === 0 && nextSong) {
            await playJingle(volumeRef.current, mutedRef.current);
            await playDjAnnouncement(nextSong, volumeRef.current, mutedRef.current);
          } else if (weatherSongCountRef.current >= WEATHER_INTERVAL) {
            weatherSongCountRef.current = 0;
            let weatherBlobUrl = prefetchedWeatherBlobRef.current;
            prefetchedWeatherBlobRef.current = null;
            if (!weatherBlobUrl) {
              weatherBlobUrl = await generateWeatherAnnouncement();
            }
            if (weatherBlobUrl) {
              if (weatherBlobUrlRef.current) URL.revokeObjectURL(weatherBlobUrlRef.current);
              weatherBlobUrlRef.current = weatherBlobUrl;
              const weatherAudio = weatherAudioRef.current;
              if (weatherAudio) {
                await new Promise<void>((res) => {
                  weatherAudio.src = weatherBlobUrl!;
                  weatherAudio.volume = mutedRef.current ? 0 : Math.max(0, Math.min(1, volumeRef.current / 100));
                  weatherAudio.currentTime = 0;
                  const wCleanup = () => { weatherAudio.onended = null; weatherAudio.onerror = null; };
                  weatherAudio.onended = () => { wCleanup(); setWeatherSpeaking(false); res(); };
                  weatherAudio.onerror = () => { wCleanup(); setWeatherSpeaking(false); res(); };
                  weatherAudio.load();
                  const onWCanPlay = () => {
                    weatherAudio.removeEventListener('canplay', onWCanPlay);
                    weatherAudio.play().then(() => setWeatherSpeaking(true)).catch(() => { setWeatherSpeaking(false); res(); });
                  };
                  weatherAudio.addEventListener('canplay', onWCanPlay, { once: true });
                });
              }
            }
          } else {
            weatherSongCountRef.current += 1;
          }

          setCurrentIndex(nextIdx);
          loadAndPlay(nextIdx, list);
        }, delay);
      };

      if (audio.duration && isFinite(audio.duration)) {
        scheduleNearEnd();
      } else {
        audio.addEventListener('loadedmetadata', scheduleNearEnd, { once: true });
      }

    } catch (err) {
      console.error('[Player] play() failed:', err);
      setIsLoading(false);
      setIsPlaying(false);
    }
  }, [startTicker, stopTicker, prebufferNext, playJingle, playStationJingle, playHitsJingle, playDjAnnouncement, reconnectStream]);

  // ── Watchdog: detect silent stalls and auto-recover ───────────────────────
  useEffect(() => {
    watchdogRef.current = setInterval(async () => {
      if (isPlayingNewsRef.current || isPlayingTopShowRef.current) return;
      if (!isPlaying) return;

      const audio = audioRef.current;
      if (!audio) return;

      const now = Date.now();
      const currentTime = audio.currentTime;

      if (!audio.paused) {
        if (currentTime === lastKnownTimeRef.current && now - lastKnownTimeStampRef.current > 15000) {
          console.warn('[Watchdog] Audio stalled — currentTime frozen for 15s');
          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
            await reconnectStream(currentIndex, songs);
          } else {
            reconnectAttemptsRef.current = 0;
            loadAndPlay(currentIndex, songs);
          }
          lastKnownTimeStampRef.current = now;
          return;
        }
        lastKnownTimeRef.current = currentTime;
        lastKnownTimeStampRef.current = now;
      } else if (isPlaying) {
        console.warn('[Watchdog] Audio paused unexpectedly — attempting recovery');
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
          await reconnectStream(currentIndex, songs);
        } else {
          reconnectAttemptsRef.current = 0;
          loadAndPlay(currentIndex, songs);
        }
      }
    }, 10000);

    return () => {
      if (watchdogRef.current) {
        clearInterval(watchdogRef.current);
        watchdogRef.current = null;
      }
    };
  }, [isPlaying, songs, currentIndex, reconnectStream, loadAndPlay]);

  // ── Page visibility: resume audio when tab becomes visible ───────────────
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (isPlayingNewsRef.current || isPlayingTopShowRef.current) return;
      if (!isPlaying) return;

      const audio = audioRef.current;
      if (!audio) return;

      if (audio.paused) {
        console.log('[Visibility] Tab became visible — resuming audio');
        try {
          await reconnectStream(currentIndex, songs);
        } catch {
          try {
            await audio.play();
            startTicker();
          } catch (err) {
            console.error('[Visibility] Failed to resume audio:', err);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPlaying, currentIndex, songs, reconnectStream, startTicker]);

  // ── Stall/waiting event listeners ────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    let stallDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    const handleStall = () => {
      if (isPlayingNewsRef.current || isPlayingTopShowRef.current) return;
      if (!isPlaying) return;
      if (stallDebounceTimer) clearTimeout(stallDebounceTimer);
      stallDebounceTimer = setTimeout(async () => {
        console.warn('[Stall] Audio stall/waiting event — attempting reconnect');
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current <= MAX_RECONNECT_ATTEMPTS) {
          await reconnectStream(currentIndex, songs);
        } else {
          reconnectAttemptsRef.current = 0;
          loadAndPlay(currentIndex, songs);
        }
      }, 5000);
    };

    audio.addEventListener('stalled', handleStall);
    audio.addEventListener('waiting', handleStall);

    return () => {
      audio.removeEventListener('stalled', handleStall);
      audio.removeEventListener('waiting', handleStall);
      if (stallDebounceTimer) clearTimeout(stallDebounceTimer);
    };
  }, [isPlaying, currentIndex, songs, reconnectStream, loadAndPlay]);

  // ── Fetch artwork for current song ───────────────────────────────────────
  useEffect(() => {
    const song = songs[currentIndex];
    if (!song) return;
    if (artworkCache[song.id] !== undefined) return;
    if (fetchingRef.current.has(song.id)) return;
    fetchingRef.current.add(song.id);

    fetchItunesArtwork(song.artist || '', song.title || '').then((url) => {
      setArtworkCache((prev) => ({ ...prev, [song.id]: url }));
      fetchingRef.current.delete(song.id);
    });
  }, [currentIndex, songs, artworkCache]);

  // ── Volume sync ───────────────────────────────────────────────────────────
  useEffect(() => {
    volumeRef.current = volume;
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : Math.max(0, Math.min(1, volume / 100));
    }
  }, [volume, muted]);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePlay = useCallback(() => {
    if (!userHasInteracted) setUserHasInteracted(true);
    if (songs.length === 0) return;
    loadAndPlay(currentIndex, songs);
  }, [userHasInteracted, songs, currentIndex, loadAndPlay]);

  const handlePause = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    stopTicker();
    setIsPlaying(false);
  }, [stopTicker]);

  const handleNext = useCallback(() => {
    if (songs.length === 0) return;
    const nextIdx = (currentIndex + 1) % songs.length;
    setCurrentIndex(nextIdx);
    if (isPlaying) loadAndPlay(nextIdx, songs);
  }, [songs, currentIndex, isPlaying, loadAndPlay]);

  const handlePrev = useCallback(() => {
    if (songs.length === 0) return;
    const prevIdx = (currentIndex - 1 + songs.length) % songs.length;
    setCurrentIndex(prevIdx);
    if (isPlaying) loadAndPlay(prevIdx, songs);
  }, [songs, currentIndex, isPlaying, loadAndPlay]);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setVolumeState(val);
    setMuted(val === 0);
  }, []);

  const handleMuteToggle = useCallback(() => {
    setMuted((prev) => !prev);
  }, []);

  const handleSeek = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
      setElapsed(val);
    }
  }, []);

  const handleSongSelect = useCallback((index: number) => {
    setCurrentIndex(index);
    setShowQueue(false);
    loadAndPlay(index, songs);
  }, [songs, loadAndPlay]);

  const currentSong = songs[currentIndex];
  const artworkUrl = currentSong ? (artworkCache[currentSong.id] ?? '') : '';
  const [gradFrom, gradTo] = currentSong ? getArtColor(currentSong.artist || currentSong.title || '') : ['#7c3aed', '#a855f7'];
  const initials = currentSong ? getInitials(currentSong.artist || '', currentSong.title || '') : '?';

  const formatTime = (s: number) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col relative overflow-hidden">
      {/* Hidden audio elements */}
      <audio ref={audioRef} style={{ display: 'none' }} />
      <audio ref={nextAudioRef} style={{ display: 'none' }} />
      <audio ref={djAudioRef} style={{ display: 'none' }} />
      <audio ref={jingleAudioRef} style={{ display: 'none' }} />
      <audio ref={stationJingleAudioRef} style={{ display: 'none' }} />
      <audio ref={hitsJingleAudioRef} style={{ display: 'none' }} />
      <audio ref={weatherAudioRef} style={{ display: 'none' }} />
      {/* News bulletin audio element */}
      <audio ref={newsAudioRef} style={{ display: 'none' }} />
      {/* Top Songs Show audio element */}
      <audio ref={topSongsAudioRef} style={{ display: 'none' }} />

      {/* Topbar */}
      <header className="flex items-center justify-between px-6 md:px-12 py-5 border-b border-border/50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center shadow-lg">
            <span className="text-primary-foreground font-black text-sm">R</span>
          </div>
          <div>
            <p className="font-bold text-foreground text-sm leading-tight">Rumbaexplosions</p>
            <p className="text-xs text-muted-foreground">Online Radio</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-muted-foreground font-medium">LIVE</span>
        </div>
      </header>

      {/* Loading overlay */}
      {!songsLoaded && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="w-16 h-16 rounded-full border-4 border-primary/30 border-t-primary animate-spin mb-4" />
          <p className="text-sm text-muted-foreground font-medium">Cargando Música...</p>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 max-w-sm mx-auto w-full">

        {/* News speaking indicator */}
        {newsSpeaking && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full animate-pulse">
            <span className="text-lg">📰</span>
            <span className="text-sm font-semibold text-primary">Noticias en vivo...</span>
            <div className="flex items-end gap-0.5 h-4">
              {[0.6, 1.0, 0.7, 0.9, 0.5, 1.0, 0.8].map((h, i) => (
                <div
                  key={`news-wave-${i}`}
                  className="w-1 rounded-full bg-primary"
                  style={{
                    height: `${h * 100}%`,
                    animation: `pulse 0.${6 + i}s ease-in-out infinite alternate`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* DJ speaking indicator */}
        {djSpeaking && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-secondary/50 border border-border rounded-full animate-pulse">
            <span className="text-lg">🎙️</span>
            <span className="text-sm font-semibold text-foreground">DJ Jorge al micrófono...</span>
            <div className="flex items-end gap-0.5 h-4">
              {[0.4, 0.8, 1.0, 0.6, 0.9, 0.7, 1.0].map((h, i) => (
                <div
                  key={`dj-wave-${i}`}
                  className="w-1 rounded-full bg-primary"
                  style={{
                    height: `${h * 100}%`,
                    animation: `pulse 0.${5 + i}s ease-in-out infinite alternate`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Weather speaking indicator */}
        {weatherSpeaking && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full animate-pulse">
            <span className="text-lg">🌤️</span>
            <span className="text-sm font-semibold text-blue-400">Reporte del clima...</span>
          </div>
        )}

        {/* Top Songs Show indicator */}
        {topShowSpeaking && (
          <div className="mb-4 flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full animate-pulse">
            <span className="text-lg">🏆</span>
            <span className="text-sm font-semibold text-yellow-400">Las 10 Más Tocadas de la Semana...</span>
            <div className="flex items-end gap-0.5 h-4">
              {[0.5, 1.0, 0.7, 0.9, 0.6, 1.0, 0.8].map((h, i) => (
                <div
                  key={`top-wave-${i}`}
                  className="w-1 rounded-full bg-yellow-400"
                  style={{
                    height: `${h * 100}%`,
                    animation: `pulse 0.${6 + i}s ease-in-out infinite alternate`,
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Album Art */}
        <div className="relative mb-6">
          {isPlaying && (
            <div
              className="absolute inset-0 rounded-full blur-2xl opacity-40 scale-110"
              style={{ background: `radial-gradient(circle, ${gradFrom}, ${gradTo})` }}
            />
          )}
          <div
            className="relative w-56 h-56 rounded-full overflow-hidden shadow-2xl border-4 border-border/30"
            style={artworkUrl ? {} : { background: `linear-gradient(135deg, ${gradFrom}, ${gradTo})` }}
          >
            {artworkUrl ? (
              <img src={artworkUrl} alt={currentSong?.title || 'Album art'} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-5xl font-black text-white/90">{initials}</span>
              </div>
            )}
            {isPlaying && (
              <div className="absolute inset-0 rounded-full border-2 border-white/10 animate-spin" style={{ animationDuration: '3s' }} />
            )}
          </div>
        </div>

        {/* Song info */}
        <div className="text-center mb-6 w-full">
          {isLoading ? (
            <div className="space-y-2">
              <div className="h-5 bg-muted rounded-full w-48 mx-auto animate-pulse" />
              <div className="h-4 bg-muted rounded-full w-32 mx-auto animate-pulse" />
            </div>
          ) : currentSong ? (
            <>
              <h2 className="text-xl font-bold text-foreground truncate px-4">{currentSong.title}</h2>
              <p className="text-sm text-muted-foreground mt-1 truncate px-4">{currentSong.artist}</p>
            </>
          ) : (
            <p className="text-muted-foreground text-sm">No songs available</p>
          )}
          {loadError && <p className="text-destructive text-xs mt-1">{loadError}</p>}
        </div>

        {/* Progress bar */}
        <div className="w-full mb-4">
          <input
            type="range"
            min={0}
            max={duration || 0}
            value={elapsed}
            onChange={handleSeek}
            className="w-full h-1.5 accent-primary cursor-pointer"
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>{formatTime(elapsed)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-6 mb-6">
          <button
            onClick={handlePrev}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Previous"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 6h2v12H6zm3.5 6 8.5 6V6z" />
            </svg>
          </button>

          <button
            onClick={isPlaying ? handlePause : handlePlay}
            disabled={isLoading || songs.length === 0}
            className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
            ) : isPlaying ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <Play size={24} className="ml-1" />
            )}
          </button>

          <button
            onClick={handleNext}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
            aria-label="Next"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 18l8.5-6L6 6v12zm2.5-6 5.5 3.9V8.1L8.5 12zM16 6h2v12h-2z" />
            </svg>
          </button>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-3 w-full mb-6">
          <button onClick={handleMuteToggle} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Toggle mute">
            {muted || volume === 0 ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3 3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4 9.91 6.09 12 8.18V4z" />
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
              </svg>
            )}
          </button>
          <input
            type="range"
            min={0}
            max={100}
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            className="flex-1 h-1.5 accent-primary cursor-pointer"
          />
          <span className="text-xs text-muted-foreground w-8 text-right">{muted ? 0 : volume}%</span>
        </div>

        {/* Queue toggle */}
        <button
          onClick={() => setShowQueue((prev) => !prev)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 13h2v-2H3v2zm0 4h2v-2H3v2zm0-8h2V7H3v2zm4 4h14v-2H7v2zm0 4h14v-2H7v2zM7 7v2h14V7H7z" />
          </svg>
          {showQueue ? 'Hide Queue' : `Queue (${songs.length})`}
        </button>

        {/* Queue list */}
        {showQueue && (
          <div className="w-full max-h-64 overflow-y-auto rounded-xl border border-border bg-card">
            {songs.map((song, idx) => (
              <button
                key={song.id}
                onClick={() => handleSongSelect(idx)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/50 transition-colors border-b border-border/50 last:border-0 ${idx === currentIndex ? 'bg-primary/10' : ''}`}
              >
                <span className="text-xs text-muted-foreground w-5 text-center flex-shrink-0">
                  {idx === currentIndex && isPlaying ? '▶' : idx + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${idx === currentIndex ? 'text-primary' : 'text-foreground'}`}>{song.title}</p>
                  <p className="text-xs text-muted-foreground truncate">{song.artist}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
